import type { TFunction } from 'i18next';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import { TimeAllocationType } from '../../../api/work-periods-client/work-periods-client.type';
import { primaryBase, redBase, yellowBase } from '../../../styles/design-tokens';

type TargetFormObject = {
  id: string;
  name: string;
  start_date?: string;
  end_date?: string;
};

type TargetForm = {
  id?: string;
  name: string;
  measure: string;
  target_value: number;
  target_value_upper: number | null;
  target_comparison: TargetComparison;
  is_active: boolean;
  use_transform: boolean;
  start_date: string | null;
  end_date: string | null;
  time_allocation_type: TimeAllocationType | null;
  is_work_period_target: boolean;
  object_type: 'portfolio' | 'project' | 'board' | 'initiative' | null;
  objects: TargetFormObject[];
  green_threshold_lower: number | undefined;
  green_threshold_upper: number | undefined;
  yellow_threshold_lower: number | undefined;
  yellow_threshold_upper: number | undefined;
  red_threshold_lower: number | undefined;
  red_threshold_upper: number | undefined;
};

const OBJECT_TYPES = [
  { value: 'board', label: 'Board' },
  { value: 'project', label: 'Project' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'initiative', label: 'Initiative' },
];

enum TargetThreshold {
  GREEN = 'green',
  YELLOW = 'yellow',
  RED = 'red',
}

/**
 * Build the threshold → display label map against the caller's translation
 * function so org-level text_config overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Record<TargetThreshold, string>} Threshold label map
 */
const getTargetThresholdLabels = (t: TFunction): Record<TargetThreshold, string> => ({
  [TargetThreshold.GREEN]: t('targets.green'),
  [TargetThreshold.YELLOW]: t('targets.yellow'),
  [TargetThreshold.RED]: t('targets.red'),
});

const TARGET_THRESHOLD_COLORS = {
  [TargetThreshold.GREEN]: primaryBase,
  [TargetThreshold.YELLOW]: yellowBase,
  [TargetThreshold.RED]: redBase,
};

export { getTargetThresholdLabels, OBJECT_TYPES, TARGET_THRESHOLD_COLORS, TargetThreshold };
export type { TargetForm, TargetFormObject };
